import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../../../components/SEO';
import { FaArrowRight, FaCalendarAlt, FaClock, FaUser, FaMapMarkerAlt, FaPhoneAlt, FaDrum } from 'react-icons/fa';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Tabla Classes in Pune with Maestro Atharva Kagalkar — Taalsetu Music Academy",
  "description": "Discover why students from across Pune and beyond choose Taalsetu Music Academy in Wakad for authentic tabla training. Learn about our teaching philosophy and class formats.",
  "image": "https://www.taalsetu.com/logo512.png",
  "datePublished": "2026-06-02",
  "dateModified": "2026-06-02",
  "author": {
    "@type": "Person",
    "name": "Atharva Kagalkar",
    "url": "https://www.taalsetu.com/#founder"
  },
  "publisher": {
    "@type": "MusicSchool",
    "@id": "https://www.taalsetu.com/#organization",
    "name": "Taalsetu Music Academy",
    "logo": { "@type": "ImageObject", "url": "https://www.taalsetu.com/logo512.png" }
  },
  "mainEntityOfPage": { "@type": "WebPage", "@id": "https://www.taalsetu.com/blog/tabla-classes-pune-atharva-kagalkar/" },
  "keywords": "tabla classes Pune, tabla teacher Pune, tabla lessons Pune, Atharva Kagalkar tabla, Wakad tabla classes, tabla classes Hinjewadi",
  "articleSection": "Tabla Education",
  "about": {
    "@type": "LocalBusiness",
    "name": "Taalsetu Music Academy",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Green Woods 302, Kaspate Vasti, Wakad",
      "addressLocality": "Pune",
      "addressRegion": "Maharashtra",
      "addressCountry": "IN"
    }
  }
};

const TablaPune = () => {
  return (
    <div className="min-h-screen font-sans bg-white dark:bg-maroon-dark">
      <SEO
        title="Tabla Classes in Pune — Maestro Atharva Kagalkar | Taalsetu Wakad"
        description="Looking for tabla classes in Pune? Taalsetu Music Academy in Wakad offers authentic tabla lessons by Maestro Atharva Kagalkar — online and offline, for all levels. Serving Wakad, Hinjewadi, Baner, Aundh and all of Pune."
        keywords="tabla classes Pune, tabla teacher Pune, tabla lessons Pune, Atharva Kagalkar tabla, tabla classes Wakad, tabla classes Hinjewadi, tabla classes Baner, best tabla teacher Pune, tabla music school Pune"
        url="https://www.taalsetu.com/blog/tabla-classes-pune-atharva-kagalkar/"
        schemaMarkup={articleSchema}
        breadcrumbs={[
          { name: 'Home', url: 'https://www.taalsetu.com/' },
          { name: 'Blog', url: 'https://www.taalsetu.com/blog/' },
          { name: 'Tabla Classes in Pune with Atharva Kagalkar', url: 'https://www.taalsetu.com/blog/tabla-classes-pune-atharva-kagalkar/' }
        ]}
      />

      {/* Hero */}
      <motion.section
        className="relative pt-28 pb-16 bg-gradient-to-br from-maroon via-maroon to-maroon-dark text-white overflow-hidden"
        initial="hidden" animate="visible" variants={fadeUp}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 bg-gold rounded-full blur-3xl" />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/blog/" className="inline-flex items-center text-gold/70 hover:text-gold text-sm mb-6 transition-colors">
            ← Back to Blog
          </Link>
          <div className="inline-block bg-green-500/20 border border-green-400/30 text-green-300 text-xs font-bold px-3 py-1 rounded-full mb-4">
            Pune & Local
          </div>
          <h1 className="font-serif text-3xl md:text-5xl font-bold leading-tight mb-6">
            Tabla Classes in Pune with Maestro Atharva Kagalkar
          </h1>
          <div className="flex flex-wrap items-center gap-5 text-sm text-gold-light/50">
            <span className="flex items-center gap-2"><FaUser className="text-gold" /> Atharva Kagalkar</span>
            <span className="flex items-center gap-2"><FaCalendarAlt className="text-gold" /> June 2, 2026</span>
            <span className="flex items-center gap-2"><FaClock className="text-gold" /> 6 min read</span>
          </div>
        </div>
      </motion.section>

      {/* Article */}
      <motion.article className="py-16" initial="hidden" animate="visible" variants={stagger}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Intro */}
          <motion.div variants={fadeUp} className="mb-10">
            <p className="text-lg text-maroon/80 dark:text-gold-light/80 leading-relaxed mb-6">
              Pune has a rich tradition of Indian classical music, and tabla holds a special place in that heritage. If you're searching for <strong className="text-maroon dark:text-gold">tabla classes in Pune</strong>, Taalsetu Music Academy in Wakad is one of the city's most trusted names in authentic tabla education.
            </p>
            <p className="text-maroon/70 dark:text-gold-light/70 leading-relaxed mb-6">
              Founded by Maestro Atharva Kagalkar, Taalsetu combines the depth of traditional gurukul-style training with the flexibility of modern scheduling — serving students across Wakad, Hinjewadi, Baner, Aundh, and online students from around the world.
            </p>
          </motion.div>

          {/* Location Info */}
          <motion.div variants={fadeUp} className="bg-maroon text-white rounded-2xl p-6 mb-10 flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <FaMapMarkerAlt className="text-gold" />
                <span className="font-semibold text-gold-light">Head Office — Wakad, Pune</span>
              </div>
              <p className="text-gold-light/70 text-sm leading-relaxed">
                Green Woods 302, Kaspate Vasti<br />
                Wakad, Pune, Maharashtra 411057<br />
                5 minutes from Hinjewadi IT Park<br />
                Near Wakad Metro Station
              </p>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <FaPhoneAlt className="text-gold" />
                <span className="font-semibold text-gold-light">Contact Us</span>
              </div>
              <p className="text-gold-light/70 text-sm mb-2">+91 82756 64288</p>
              <p className="text-gold-light/70 text-sm mb-4">taalsetu@gmail.com</p>
              <p className="text-gold-light/50 text-xs">Open: 8am – 10pm, Monday to Sunday</p>
            </div>
          </motion.div>

          {/* About Atharva */}
          <motion.div variants={fadeUp} className="mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-maroon dark:text-gold-light mb-4">
              Meet Your Teacher: Maestro Atharva Kagalkar
            </h2>
            <div className="gold-divider mb-6" />
            <p className="text-maroon/70 dark:text-gold-light/70 leading-relaxed mb-4">
              Maestro Atharva Kagalkar is a trained classical tabla artist with deep roots in the Hindustani percussion tradition. His teaching philosophy blends the strict discipline of gurukul learning with a patient, student-centred approach that makes even complex concepts accessible.
            </p>
            <p className="text-maroon/70 dark:text-gold-light/70 leading-relaxed mb-4">
              He teaches students of all ages — from young children as early as 5 years old, to working professionals starting at 40+. Whether you're a complete beginner or preparing for a performance examination, Maestro Kagalkar crafts a personalised learning plan for each student.
            </p>
            <div className="bg-gold/8 border border-gold/20 rounded-xl p-6">
              <p className="text-maroon/80 dark:text-gold-light/80 text-sm italic">
                "My goal is to make every student fall in love with the tabla — not just learn it mechanically. The rhythm lives inside you; my job is to bring it out." — Maestro Atharva Kagalkar
              </p>
            </div>
          </motion.div>

          {/* Class formats */}
          <motion.div variants={fadeUp} className="mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-maroon dark:text-gold-light mb-4">
              Class Formats Available in Pune
            </h2>
            <div className="gold-divider mb-6" />
            <div className="grid md:grid-cols-2 gap-5">
              {[
                {
                  format: "In-Person (Wakad, Pune)",
                  desc: "Face-to-face lessons at our Wakad studio. Ideal for Pune residents — the best way to learn correct technique.",
                  badge: "Most Popular",
                  badgeClass: "bg-gold text-maroon",
                },
                {
                  format: "Online (One-on-One)",
                  desc: "Live lessons via Zoom. Same teacher, same curriculum, same quality — available anywhere in the world.",
                  badge: "Global Students",
                  badgeClass: "bg-blue-500/20 text-blue-300",
                },
                {
                  format: "Batch Classes (Pune)",
                  desc: "Small group classes (2–4 students) for a more social learning experience. Available for beginners.",
                  badge: "Budget Friendly",
                  badgeClass: "bg-green-500/20 text-green-300",
                },
                {
                  format: "Intensive Weekend Workshops",
                  desc: "Focused weekend sessions covering a specific topic — taal, composition, or performance technique.",
                  badge: "Special Events",
                  badgeClass: "bg-purple-500/20 text-purple-300",
                },
              ].map((cls, i) => (
                <div key={i} className="border border-gold/10 rounded-xl p-5 hover:border-gold/30 transition-all">
                  <div className={`text-xs font-bold px-2 py-1 rounded-full inline-block mb-3 ${cls.badgeClass}`}>{cls.badge}</div>
                  <h3 className="font-serif font-bold text-maroon dark:text-gold-light mb-2">{cls.format}</h3>
                  <p className="text-sm text-maroon/60 dark:text-gold-light/60">{cls.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Areas served */}
          <motion.div variants={fadeUp} className="mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-maroon dark:text-gold-light mb-4">
              Tabla Lessons Across Pune
            </h2>
            <div className="gold-divider mb-6" />
            <p className="text-maroon/70 dark:text-gold-light/70 leading-relaxed mb-4">
              Our Wakad studio is easily accessible from across Pune's western corridor. Students regularly travel from:
            </p>
            <div className="flex flex-wrap gap-3">
              {["Wakad", "Hinjewadi", "Baner", "Aundh", "Pimple Saudagar", "Pimple Nilakh", "Ravet", "Tathawade", "Punawale", "Chinchwad", "Kothrud"].map((area) => (
                <span key={area} className="bg-maroon/5 dark:bg-white/5 border border-gold/15 text-maroon/70 dark:text-gold-light/70 text-sm px-4 py-2 rounded-full">
                  {area}
                </span>
              ))}
            </div>
            <p className="text-sm text-maroon/50 dark:text-gold-light/50 mt-4">
              Outside Pune? We offer the same quality experience through our online classes.
            </p>
          </motion.div>

          {/* CTA */}
          <motion.div variants={fadeUp} className="bg-maroon rounded-2xl p-8 text-white text-center mt-12">
            <FaDrum className="text-gold text-4xl mx-auto mb-4" />
            <h3 className="font-serif text-2xl font-bold mb-3">Book a Free Trial Class in Pune</h3>
            <p className="text-gold-light/70 mb-6">In-person at Wakad or online — your first 15-minute trial is free.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:+918275664288" className="btn-gold !rounded-xl flex items-center px-8 py-3">
                <FaPhoneAlt className="mr-2" /> Call Now
              </a>
              <Link to="/register" className="px-8 py-3 border border-gold/40 text-gold-light rounded-xl hover:bg-gold/10 transition-all">
                Register Online
              </Link>
            </div>
          </motion.div>

          {/* More Articles */}
          <motion.div variants={fadeUp} className="mt-14 pt-10 border-t border-gold/10">
            <h3 className="font-serif text-xl font-bold text-maroon dark:text-gold-light mb-6">More Articles</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/blog/learn-tabla-online-beginners/" className="group border border-gold/10 rounded-xl p-5 hover:border-gold/30 transition-all">
                <p className="text-xs text-gold font-bold uppercase tracking-wider mb-2">Beginners Guide</p>
                <p className="font-serif font-bold text-maroon dark:text-gold-light group-hover:text-gold transition-colors text-sm leading-snug">How to Learn Tabla Online as a Complete Beginner</p>
              </Link>
              <Link to="/blog/online-tabla-classes-for-kids/" className="group border border-gold/10 rounded-xl p-5 hover:border-gold/30 transition-all">
                <p className="text-xs text-gold font-bold uppercase tracking-wider mb-2">Kids & Families</p>
                <p className="font-serif font-bold text-maroon dark:text-gold-light group-hover:text-gold transition-colors text-sm leading-snug">Online Tabla Classes for Kids — A Complete Parent's Guide</p>
              </Link>
            </div>
          </motion.div>
        </div>
      </motion.article>
    </div>
  );
};

export default TablaPune;
